import { useState, useEffect } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Plus, Pencil, Trash2, Search, EyeOff, Eye, Filter } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";

export default function AdminCountries() {
  const [countries, setCountries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({ name: "", flag_url: "" });
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | disabled | empty
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: () => {} });
  const itemsPerPage = 20;

  useEffect(() => { fetchCountries(); }, []);

  const fetchCountries = () => {
    axios.get(`${API}/countries?include_counts=true&include_disabled=true`, { withCredentials: true }).then((res) => setCountries(res.data));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editingCountry ? "put" : "post";
    const url = editingCountry ? `${API}/countries/${editingCountry.id}` : `${API}/countries`;
    await axios[method](url, formData, { withCredentials: true });
    setIsModalOpen(false);
    setEditingCountry(null);
    setFormData({ name: "", flag_url: "" });
    fetchCountries();
  };

  const handleDelete = (id) => {
    setConfirmModal({
      isOpen: true, title: "Delete Country", message: "Are you sure? This cannot be undone.",
      onConfirm: async () => {
        await axios.delete(`${API}/countries/${id}`, { withCredentials: true });
        fetchCountries();
        setSelectedIds(prev => prev.filter(s => s !== id));
      }
    });
  };

  const handleBulkDelete = () => {
    setConfirmModal({
      isOpen: true, title: "Delete Countries", message: `Delete ${selectedIds.length} countries permanently?`,
      onConfirm: async () => {
        await Promise.all(selectedIds.map(id => axios.delete(`${API}/countries/${id}`, { withCredentials: true })));
        setSelectedIds([]);
        fetchCountries();
      }
    });
  };

  const handleBulkDisable = () => {
    setConfirmModal({
      isOpen: true, title: "Disable Countries",
      message: `Disable ${selectedIds.length} countries? They will be hidden from the public site.`,
      onConfirm: async () => {
        await axios.post(`${API}/countries/bulk-disable`, { ids: selectedIds }, { withCredentials: true });
        setSelectedIds([]);
        fetchCountries();
      }
    });
  };

  const handleBulkEnable = () => {
    setConfirmModal({
      isOpen: true, title: "Enable Countries",
      message: `Enable ${selectedIds.length} countries? They will be visible on the public site.`,
      onConfirm: async () => {
        await axios.post(`${API}/countries/bulk-enable`, { ids: selectedIds }, { withCredentials: true });
        setSelectedIds([]);
        fetchCountries();
      }
    });
  };

  const selectEmpty = () => {
    const emptyIds = countries.filter(c => (c.channel_count || 0) === 0).map(c => c.id);
    setSelectedIds(emptyIds);
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const filtered = countries
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    .filter(c => {
      if (statusFilter === "active") return c.is_active !== 0;
      if (statusFilter === "disabled") return c.is_active === 0;
      if (statusFilter === "empty") return (c.channel_count || 0) === 0;
      return true;
    });

  const toggleAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map(c => c.id));
  };

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const activeCount = countries.filter(c => c.is_active !== 0).length;
  const disabledCount = countries.filter(c => c.is_active === 0).length;
  const emptyCount = countries.filter(c => (c.channel_count || 0) === 0).length;

  return (
    <>
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-white">{countries.length}</div>
          <div className="text-xs text-slate-500">Total</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-emerald-400">{activeCount}</div>
          <div className="text-xs text-slate-500">Active</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-red-400">{disabledCount}</div>
          <div className="text-xs text-slate-500">Disabled</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 cursor-pointer hover:border-amber-500/30 transition-colors" onClick={() => { setStatusFilter("empty"); setCurrentPage(1); }}>
          <div className="text-2xl font-bold text-amber-400">{emptyCount}</div>
          <div className="text-xs text-slate-500">Empty (0 channels)</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text" placeholder="Search countries..." data-testid="country-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select
            value={statusFilter} data-testid="country-status-filter"
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); setSelectedIds([]); }}
            className="px-4 py-2.5 rounded-xl border border-slate-800 bg-slate-900 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="all">All Countries ({countries.length})</option>
            <option value="active">Active ({activeCount})</option>
            <option value="disabled">Disabled ({disabledCount})</option>
            <option value="empty">Empty - 0 Channels ({emptyCount})</option>
          </select>
          <button
            onClick={() => { setEditingCountry(null); setFormData({ name: "", flag_url: "" }); setIsModalOpen(true); }}
            data-testid="add-country-btn"
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20 whitespace-nowrap"
          >
            <Plus size={18} /> Add Country
          </button>
        </div>

        {/* Bulk actions */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={selectEmpty} data-testid="select-empty-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-xs font-medium hover:bg-amber-500/20 transition-colors">
            <Filter size={14} /> Select All Empty ({emptyCount})
          </button>
          {selectedIds.length > 0 && (
            <>
              <span className="text-xs text-blue-400 font-medium bg-blue-500/10 px-2 py-1.5 rounded-lg">{selectedIds.length} selected</span>
              <button onClick={handleBulkDisable} data-testid="bulk-disable-btn"
                className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-600 hover:text-white transition-colors">
                <EyeOff size={14} /> Disable Selected
              </button>
              <button onClick={handleBulkEnable} data-testid="bulk-enable-btn"
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-medium hover:bg-emerald-600 hover:text-white transition-colors">
                <Eye size={14} /> Enable Selected
              </button>
              <button onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-600 hover:text-white transition-colors">
                <Trash2 size={14} /> Delete Selected
              </button>
              <button onClick={() => setSelectedIds([])}
                className="text-xs text-slate-500 hover:text-white transition-colors underline">Clear</button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="countries-table">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800">
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-10">
                  <input type="checkbox"
                    className="w-4 h-4 rounded border-slate-800 bg-slate-950 text-blue-600 focus:ring-blue-500/20"
                    checked={selectedIds.length > 0 && selectedIds.length === filtered.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Country</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Channels</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Status</th>
                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {paginated.map((country) => (
                <tr key={country.id} className={cn("transition-colors", country.is_active === 0 ? "opacity-50 bg-red-950/10" : "hover:bg-slate-800/50")} data-testid={`country-row-${country.id}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox"
                      className="w-4 h-4 rounded border-slate-800 bg-slate-950 text-blue-600 focus:ring-blue-500/20"
                      checked={selectedIds.includes(country.id)}
                      onChange={() => toggleSelection(country.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {country.flag_url && <img src={country.flag_url} alt="" className="w-7 h-5 object-cover rounded-sm border border-slate-800" referrerPolicy="no-referrer" />}
                      <span className="font-medium text-white text-sm">{country.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn("text-sm font-bold", (country.channel_count || 0) === 0 ? "text-slate-600" : "text-white")}>
                      {country.channel_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {country.is_active === 0 ? (
                      <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-400 text-xs font-bold">Disabled</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs font-bold">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditingCountry(country); setFormData({ name: country.name, flag_url: country.flag_url }); setIsModalOpen(true); }}
                        className="p-2 text-slate-400 hover:text-blue-400 transition-colors"><Pencil size={16} /></button>
                      <button onClick={() => handleDelete(country.id)}
                        className="p-2 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-950/50">
            <div className="text-sm text-slate-400">
              Showing <span className="font-medium text-white">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium text-white">{Math.min(currentPage * itemsPerPage, filtered.length)}</span> of <span className="font-medium text-white">{filtered.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}
                className="px-3 py-1 rounded-lg border border-slate-800 bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Previous</button>
              <span className="text-sm text-slate-500">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-lg border border-slate-800 bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl border border-slate-800 overflow-hidden animate-slideUp">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
              <h3 className="font-bold text-xl text-white">{editingCountry ? "Edit Country" : "Add Country"}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white transition-colors"><Plus size={24} className="rotate-45" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-400">Country Name</label>
                <input required type="text" data-testid="country-name-input"
                  className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-400">Flag URL</label>
                <input required type="url" data-testid="country-flag-input"
                  className="w-full px-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={formData.flag_url} onChange={(e) => setFormData({ ...formData, flag_url: e.target.value })} />
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)}
                  className="flex-grow px-6 py-3 rounded-xl border border-slate-800 font-bold text-slate-400 hover:bg-slate-800 hover:text-white transition-all">Cancel</button>
                <button type="submit" data-testid="country-submit-btn"
                  className="flex-grow px-6 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20">
                  {editingCountry ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message}
        onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))} />
    </>
  );
}
